import {
  BookmarkSimple,
  Copy,
  Exam,
  PencilLine,
  Trash,
} from "@phosphor-icons/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { actions } from "astro:actions";
import Link from "astro-typesafe-routes/link/react";

import { MenuAction } from "~/components/Menu";
import { useLocale } from "~/hooks/locale";
import type { FragmentType } from "~/utils/fragment";
import { slugify } from "~/utils/slug";

import type { OmitAction, PackAction } from "../PackActions";
import { BookmarkPack } from "./BookmarkPack";
import type { UserPackActions_PackFragment } from "./UserPackActions_PackFragment";

export const UserPackActions: React.FC<{
  omit: OmitAction[];
  setAction: React.Dispatch<PackAction>;
  pack: FragmentType<"Pack", { select: typeof UserPackActions_PackFragment }>;
}> = ({ pack, setAction, omit }) => {
  const { t } = useLocale();
  const { data } = useSuspenseQuery({
    staleTime: Infinity,
    queryKey: ["bookmarks"],
    queryFn: actions.me.getBookmarks.orThrow,
  });

  // check if the current user owns the pack
  const isOwn = pack.user.id === data?.id;

  // you can only duplicate a pack if you're the owner or it's public and not original
  const canDuplicate = isOwn || (!pack.private && !pack.original);

  return (
    <>
      <li>
        {data ? (
          <BookmarkPack
            packId={pack.id}
            isBookmarked={data.bookmarks.some(
              (star) => star.packId === pack.id,
            )}
          />
        ) : (
          <MenuAction
            onClick={() => {
              setAction("login");
            }}
            icon={BookmarkSimple}
          >
            {t("bookmark")}
          </MenuAction>
        )}
      </li>
      {!omit.includes("duplicate") && canDuplicate && (
        <li>
          <MenuAction
            icon={Copy}
            onClick={() => {
              setAction(data ? "duplicate" : "login");
            }}
          >
            {t("duplicate")}
          </MenuAction>
        </li>
      )}
      {!omit.includes("assign") && (
        <li>
          <MenuAction
            icon={Exam}
            onClick={() => {
              setAction(data ? "assign" : "login");
            }}
          >
            {t("assign")}
          </MenuAction>
        </li>
      )}
      {isOwn && (
        <>
          {!omit.includes("edit") && (
            <li>
              <Link to="/account/packs/[slug]" params={{ slug: slugify(pack) }}>
                <PencilLine weight="bold" className="size-4" />
                {t("edit")}
              </Link>
            </li>
          )}
          {!omit.includes("delete") && (
            <li className="text-error">
              <MenuAction
                icon={Trash}
                onClick={() => {
                  setAction("delete");
                }}
              >
                {t("delete")}
              </MenuAction>
            </li>
          )}
        </>
      )}
    </>
  );
};
